/**
 * King Living Radio Theme
 *
 * Global radio button styling system supporting:
 * - 4 states: default, selected, default disabled, selected disabled
 * - 32px circular shape
 * - White background for all states
 * - Light grey border for active states
 * - Darker grey border for disabled states
 * - Black inner dot when selected
 * - Grey inner dot when selected and disabled
 *
 * @override Extends @salesforce/retail-react-app radio theme
 */

import baseRadio from '@salesforce/retail-react-app/app/theme/components/base/radio';

export default {
  ...baseRadio,

  baseStyle: {
    ...baseRadio.baseStyle,
    control: {
      ...baseRadio.baseStyle.control,
      // Default state: white background, light grey border
      w: 8, // 32px
      h: 8, // 32px
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'brandColors.greyscale30', // Light grey border
      backgroundColor: 'white',
      borderRadius: 'full', // Circular
      transition: 'all 0.2s ease',

      // Selected state: white background, light grey border, black inner dot (22px)
      _checked: {
        backgroundColor: 'white', // White background
        borderColor: 'brandColors.greyscale30', // Light grey border (same as default)
        color: 'brandColors.greyscale', // Black inner dot (pseudo-element)
        _hover: {
          backgroundColor: 'white',
          borderColor: 'brandColors.greyscale30',
        },
        // Target the pseudo-element (inner dot) to set size to 22px
        _before: {
          width: '22px',
          height: '22px',
        },
        // Selected disabled state: white background, darker grey border, grey inner dot (22px)
        _disabled: {
          backgroundColor: 'white', // White background
          borderColor: 'brandColors.greyscale50', // Darker grey border
          color: 'brandColors.greyscale50', // Grey inner dot
          cursor: 'not-allowed',
          opacity: 1,
          // Target the pseudo-element (inner dot) in disabled state
          _before: {
            width: '22px',
            height: '22px',
          },
        },
      },

      // Default disabled state: white background, darker grey border
      _disabled: {
        backgroundColor: 'white',
        borderColor: 'brandColors.greyscale50', // Darker grey border for disabled
        cursor: 'not-allowed',
        opacity: 1,
      },
    },
    label: {
      ...baseRadio.baseStyle.label,
      display: 'flex',
      alignItems: 'center', // Center align label text vertically
      lineHeight: 'normal', // Reset line height to prevent text from moving up
      margin: 0, // Remove default margin
      marginLeft: 3, // 12px gap from radio (matches base theme)
      padding: 0, // Remove default padding
    },
    container: {
      ...baseRadio.baseStyle.container,
      alignItems: 'center', // Center align radio and label vertically (override flex-start)
      display: 'flex',
      margin: 0, // Remove default margin
      padding: 0, // Remove default padding
    },
  },

  sizes: {
    ...baseRadio.sizes,
    md: {
      ...baseRadio.sizes.md,
      container: {
        ...baseRadio.sizes.md.container,
        alignItems: 'center', // Override flex-start to center align
      },
      control: {
        ...baseRadio.sizes.md.control,
        w: 8, // 32px
        h: 8, // 32px
        marginTop: 0, // Remove default margin-top that causes misalignment
      },
      label: {
        ...baseRadio.sizes.md.label,
        marginLeft: 3, // 12px gap from radio
        marginTop: 0, // Remove any default margin-top
      },
    },
  },
};
